import { z } from 'zod'

export function padNumber(value: number) {
    return String(value).padStart(2, '0')
}

export function getTodayDateString() {
    const now = new Date()
    return `${now.getFullYear()}-${padNumber(now.getMonth() + 1)}-${padNumber(now.getDate())}`
}

export const schedulePipelineSchema = z
    .object({
        mode: z.enum(['onetime', 'repeated']),
        startDate: z.string().optional(),
        startTime: z.string().optional(),
        frequency: z.enum(['daily', 'hourly', 'weekly', 'minute-wise']).optional(),
        frequencyhrs: z.number().optional(),
    })
    .superRefine((values, ctx) => {
        if (!values.startDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['startDate'],
                message: 'Start date is required for scheduled runs.',
            })
        }

        if (!values.startTime) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['startTime'],
                message: 'Start time is required for scheduled runs.',
            })
        }

        if (values.startDate) {
            const todayDate = getTodayDateString()
            if (values.startDate < todayDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['startDate'],
                    message: 'Selected date cannot be in the past.',
                })
            }
        }

        if (values.mode === 'repeated' && !values.frequency) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['frequency'],
                message: 'Frequency is required for repeated schedule.',
            })
        }

        if (
            values.mode === 'repeated' &&
            (values.frequency === 'hourly' || values.frequency === 'minute-wise') &&
            (!values.frequencyhrs || values.frequencyhrs <= 0)
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['frequencyhrs'],
                message: values.frequency === 'hourly'
                    ? 'Frequency hours is required.'
                    : 'Frequency minutes is required.',
            })
        }

        if (values.startDate && values.startTime) {
            const selectedDateTime = new Date(`${values.startDate}T${values.startTime}`)
            const currentDateTime = new Date()
            if (
                !Number.isNaN(selectedDateTime.getTime()) &&
                values.startDate === getTodayDateString() &&
                selectedDateTime < currentDateTime
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['startTime'],
                    message: 'Selected time cannot be in the past for current date.',
                })
            }
        }
    })

export type SchedulePipelineFormValues = z.infer<typeof schedulePipelineSchema>
